import type { Airport } from '@trips/refdata';
import { newId } from '@trips/shared/ids';
import type { Item, Place } from '@trips/shared/schema';
import { overlappingTrip, planDates, widenedDates } from '@trips/shared/trip-match';
import { patch, put } from './mutate';
import { airportCity, airportRegionCode, countryName } from './refdata';
import { selectHome, selectSettings } from './selectors';
import { getState } from './store';

/** The place record for an airport, creating it from the bundled dataset if needed. */
export async function ensureAirportPlace(a: Airport): Promise<Place> {
  const id = `plc_air_${a.iata}`;
  const existing = getState().tables.places.get(id);
  if (existing && !existing.deleted) return existing;
  return put('places', {
    id,
    name: a.name,
    address: null,
    lat: a.lat,
    lon: a.lon,
    tz: a.tz,
    city: airportCity(a),
    region: a.region || null,
    regionCode: airportRegionCode(a),
    country: countryName(a.country),
    countryCode: a.country,
    iata: a.iata,
    kind: 'airport',
    geo: 'dataset',
  });
}

/**
 * Picks the trip a new plan belongs to, by the same rule as the server: one whose dates it
 * touches within the gap setting, or one still open because its last flight left me away from
 * home; else a new trip.
 */
export async function tripForNewItem(
  item: Pick<Item, 'startAt' | 'startTz' | 'endAt' | 'endTz'>,
): Promise<string> {
  const range = planDates(item);
  if (range) {
    const s = getState();
    const settings = selectSettings(s);
    const live = <T extends { deleted: boolean }>(m: Map<string, T>) =>
      [...m.values()].filter((r) => !r.deleted);
    const match = overlappingTrip(
      range,
      live(s.tables.trips),
      live(s.tables.items),
      settings.tripGapDays,
      {
        home: selectHome(s)?.place ?? null,
        radiusMi: settings.tripRadiusMi,
        place: (id) => (id ? s.tables.places.get(id) : undefined),
      },
    );
    if (match) return match.id;
  }
  const trip = await put('trips', {
    id: newId('trp'),
    title: null,
    startDate: null,
    endDate: null,
    coverAttachmentId: null,
    notes: '',
    pinned: false,
  });
  return trip.id;
}

/** Stretches a trip with fixed dates so a plan in it falls inside them (never shrinks it). */
export async function widenTripFor(
  tripId: string | null,
  item: Pick<Item, 'startAt' | 'startTz' | 'endAt' | 'endTz'>,
): Promise<void> {
  if (!tripId) return;
  const trip = getState().tables.trips.get(tripId);
  const range = planDates(item);
  const fields = trip && !trip.deleted && range ? widenedDates(trip, range) : null;
  if (fields) await patch('trips', tripId, fields);
}
